import React, {ReactNode, useState, useEffect, useMemo, useCallback} from 'react';
import Select, {createFilter} from 'react-select';
import {CSSObject} from '@emotion/react';
import Button from '@/components/ui/Button';
import {Tooltip} from '@/components/tooltips';
import Col from 'react-bootstrap/Col';
import Collapse from 'react-bootstrap/Collapse';
import Form from 'react-bootstrap/Form';
import SmartMultiInput from '@/components/smart_multiinput/SmartMultiInput';
import useData from '@/utils/useData';
import {useStoreActions} from 'store';
import {ChartFilterType, EventFilter, EventFilterGroup, filterSelectionTypes} from '@/views/storyboard/utils/chartConstants';
import {OptionProps} from 'react-select/dist/declarations/src/components/Option';
import {MultiValue} from 'react-select/dist/declarations/src/types';
import {FormikT, FormikTValues} from 'utils/types';
import {FormikValues} from 'formik/dist/types';
import {AdditionalFilter, AllFields} from 'entities/chartFilters';
import {produce} from 'immer';
import PrioritiseButton from '@/components/utils/PrioritiseButton';
import {useAlert} from '@/components/alerts';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {isFieldRestrictedByKeys} from '@/utils/fieldRegistry';
import {RestrictedFieldInput} from '@/components/utils/DataNotAvailable';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';
import ChartFilterDropdown, {
    ChartSettingsFilterGroup,
    ChartSettingsFilterOption,
} from '@/views/storyboard/chart_settings/ChartFilterDropdown';
import ChartSettingsMultiFilterSelect from '@/views/storyboard/chart_settings/ChartSettingsMultiFilterSelect';


export interface SelectOption {
    label: ReactNode;
    value: string;
    type: ChartFilterType;
    labelClean?: string;
    label_clean?: string;
    description?: string | null;
    isDisabled?: boolean;
}

const getEventFilterGroupLabel = (key: string): string => {
    switch (key) {
        case 'core':
            return i18n.t('ft_label_core.text');
        case 'prioritised':
            return i18n.t('ft_label_evaluations.text');
        case 'non_prioritised':
            return i18n.t('ft_label_evaluations_not_prioritised.text');
        case 'others':
            return i18n.t('ft_label_others.text');
        default:
            return key;
    }
};

const stripFilterDescription = (description: string | null | undefined): string =>
    description ? description.replace(/<[^>]+>/g, '') : i18n.t('ft_no_desc.text');


interface Props<FVT extends FormikValues> {
    // TODO Phase out all_fields (note the underscore) when fitting
    allFieldsName?: 'allFields' | 'all_fields';
    formValues: FormikTValues<FVT>;
    setValues: FormikT<FVT>['setValues'];
    setFieldValue: FormikT<FVT>['setFieldValue'];

    addFiltersLabelText?: string;
    requestParams?: {
        params?: {
            includeTextSearchFilters: boolean;
        };
    };
    widgetsResetKey?: string;
    oneFilterColSize?: number;
    enableCollapse: boolean;
    parent: string;
    saveButtonContent?: ReactNode;
    pickerLayout?: 'dropdown' | 'chart-settings';
}

function EventFiltersBundle<FormikValuesType extends FormikValues = FormikValues>({
    formValues,
    setValues,
    setFieldValue,
    allFieldsName = 'all_fields',
    addFiltersLabelText = 'Add filters',
    requestParams = {},
    widgetsResetKey = '',
    oneFilterColSize = 3,
    enableCollapse = true,
    parent,
    saveButtonContent,
    pickerLayout = 'dropdown',
}: Props<FormikValuesType>) {
    const {showWarning} = useAlert();
    const filterCount = formValues.additionalFilters?.length || 0;
    const {userData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];

    const ENTITY_SHORTHAND_TO_KEY: Record<string, string> = {
        'customer': 'customer_pii',
        'agent': 'agent_pii',
        'team': 'team_pii',
    };
    const [eventFilterGroupList, loading] = useData<EventFilterGroup>(window.URLS.eventFiltersResource,
        {
            storeResolver: 'eventFilters',
            actionResolver: 'loadEventFilters',
            useCachedData: false,
            requestParams,
        });

    const isFieldNameRestricted = (fieldName: string): boolean => {
        if (!restrictedFieldKeys.length) return false;
        const shorthandKey = ENTITY_SHORTHAND_TO_KEY[fieldName];
        if (shorthandKey && restrictedFieldKeys.includes(shorthandKey)) return true;
        return isFieldRestrictedByKeys(fieldName, restrictedFieldKeys);
    };

    const isFilterRestricted = (filter: EventFilter): boolean => {
        const fieldName = filter[0].replace('all_fields.', '').replace('.keyword', '');
        return isFieldNameRestricted(fieldName);
    };

    const eventFilters: EventFilter[] = [
        ...(eventFilterGroupList?.core || []),
        ...(eventFilterGroupList?.prioritised || []),
        ...(eventFilterGroupList?.non_prioritised || []),
        ...(eventFilterGroupList?.others || []),
    ];

    const setSamplesOpen = useStoreActions((actions) => actions.queryHelpModal.setQueryHelpModalOpen);

    const [mdColSize, setMdColSize] = useState(() => {
        if (oneFilterColSize >= 6) {
            return 12;
        } else if (oneFilterColSize >= 3) {
            return 6;
        } else {
            return 4;
        }
    });

    const [lgColSize, setLgColSize] = useState(oneFilterColSize);
    const [additionalFilters, setAdditionalFilters] = useState([]);

    const onAdditionalFilterChange = (value: MultiValue<SelectOption>, actionMeta?: any) => {
        try {
            if (value) {
                if (value.length > 5) {
                    showWarning(i18n.t('event_exceed_max_filter.text'));
                    return;
                }
            }

            let filteredValues = formValues;

            const allFields = formValues[allFieldsName] as AllFields;

            if (allFields) {
                let valueFields: string[] = [];
                if (value) {
                    valueFields = value.map((obj) => obj.value.replace(`${allFieldsName}.`, ''));
                }

                const allFieldsKeys = Object.keys(allFields);

                // Remove filter values when the additional filter is removed
                filteredValues = produce(filteredValues, (draft) => {
                    allFieldsKeys.forEach((key) => {
                        if (!valueFields.some(el => el.includes(key))) {
                            delete (draft as any)[allFieldsName][key];
                        }
                    });
                });
            }

            if (value) {
                const updatedValue = value.map((obj) => ({
                    ...obj,
                    label: obj.labelClean || obj.label_clean || obj.label,
                }));

                // Update additionalFilters using immer
                const finalValues = produce(filteredValues, (draft) => {
                    // @ts-ignore
                    draft.additionalFilters = updatedValue;
                });

                setValues(finalValues);
            }
        } catch (error) {
            console.error(error);
            return;
        }
    };

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filtersBtnText, setFiltersBtnText] = useState(i18n.t('bt_open_filters.text'));

    function toggleFiltersCollapse() {
        setFiltersOpen(prevState => !prevState);
        const saveFiltersBtn = document.getElementById('save-filters-btn');
        if (saveFiltersBtn) {
            if (saveFiltersBtn.classList.contains('hide')) {
                saveFiltersBtn.classList.remove('hide');
            } else {
                saveFiltersBtn.classList.add('hide');
            }
        }
        setFiltersBtnText(prevState =>
            prevState === i18n.t('bt_open_filters.text')
                ? i18n.t('bt_close_filters.text')
                : i18n.t('bt_open_filters.text')
        );
    }

    const eventFilterBundlePlaceholder = formValues.additionalFilters?.length
        ? `${formValues.additionalFilters.length} ${i18n.t('ft_add_filters.text')}${formValues.additionalFilters.length > 1 ? 's' : ''} ${i18n.t('ft_filter_selected.text')}`
        : i18n.t('bt_add_filters.text');

    const selectedFilterValues = (formValues.additionalFilters || []).map((obj: AdditionalFilter) => obj.value);

    const chartSettingsFilterGroups = useMemo((): ChartSettingsFilterGroup[] => {
        if (!eventFilterGroupList) {
            return [];
        }

        return Object.entries(eventFilterGroupList)
            .map(([key, filters]) => ({
                key,
                label: getEventFilterGroupLabel(key),
                options: (filters || [])
                    .filter((el) => el && el[4] && !selectedFilterValues.includes(el[0]))
                    .map((el) => ({
                        value: el[0].replace('all_fields.', `${allFieldsName}.`),
                        label: el[1] || el[0],
                        description: stripFilterDescription(el[3]),
                        type: el[2] as ChartFilterType,
                        isPriority: key === 'prioritised',
                    })),
            }))
            .filter((group) => group.options.length > 0);
    }, [eventFilterGroupList, selectedFilterValues, allFieldsName]);

    const handleChartSettingsFilterSelect = useCallback((option: ChartSettingsFilterOption) => {
        onAdditionalFilterChange([
            ...(formValues.additionalFilters || []),
            {
                value: option.value,
                label: option.label,
                labelClean: option.label,
                type: option.type,
                description: option.description,
            },
        ]);
    }, [formValues.additionalFilters, onAdditionalFilterChange]);

    let formValueKeys: string | string[] = []

    // @ts-ignore
    const getValue = (formValues, allFieldsName, renamedObj) => {
        const key = renamedObj.value.replace(`${allFieldsName}.`, '');
        let value = formValues[allFieldsName] ? formValues[allFieldsName][key] : [];

        // Split the key to handle nested structures
        const keyParts = key.split('.');

        if (keyParts.length > 1) {
            value = formValues[allFieldsName];

            for (const part of keyParts) {
                if (value && value.hasOwnProperty(part)) {
                    value = value[part];
                } else {
                    value = [];
                    break;
                }
            }
        }
        return value;
    };

    useEffect(() => {
        if (formValues.additionalFilters) {
            formValueKeys = formValues.additionalFilters.map((obj: AdditionalFilter, idx: number) => obj.value)

            setAdditionalFilters(formValues.additionalFilters.map((obj: AdditionalFilter, idx: number) => {
                const renamedObj = {...obj};
                renamedObj.value = renamedObj.value.replace('all_fields.', `${allFieldsName}.`);
                const dbRewritedFilterName = eventFilters.find((el) => el[0] === renamedObj.value);

                const filterFieldName = (obj.value || '').replace(`${allFieldsName}.`, '').replace('all_fields.', '').replace('.keyword', '');
                const filterIsRestricted = isFieldNameRestricted(filterFieldName);

                if (filterIsRestricted) {
                    return (
                        <Col xs={12} md={mdColSize} xl={lgColSize} key={renamedObj.label || renamedObj.value}>
                            <Form.Group controlId={renamedObj.value} key={renamedObj.value} className='form-group'>
                                <Form.Label className='w-100 text-break'>
                                    {dbRewritedFilterName ? dbRewritedFilterName[1] : (renamedObj.label || renamedObj.value)}
                                    <span style={{paddingRight: '5px'}}>
                                        <Tooltip
                                            content={obj.description || i18n.t('ft_no_desc.text')}
                                            placement='top'
                                        >
                                            <button
                                                type='button'
                                                className='btn btn-link p-0 ml-2 text-info d-inline-flex align-items-center'
                                            >
                                                <Icon name="mdi-information-outline" size="1em" />
                                            </button>
                                        </Tooltip>
                                    </span>
                                    <Button
                                        variant="link"
                                        size="xs"
                                        className='plain-button float-right'
                                        title={i18n.t('ft_remove_filter.text')}
                                        onClick={() => {
                                            onAdditionalFilterChange(
                                                // @ts-ignore
                                                produce(formValues.additionalFilters, (draft) => {
                                                    draft.splice(idx, 1);
                                                })
                                            );
                                        }}
                                    >
                                        <Icon name="mdi-close" className="text-danger" />
                                    </Button>
                                </Form.Label>
                                <RestrictedFieldInput />
                            </Form.Group>
                        </Col>
                    );
                }

                return (
                    <Col xs={12} md={mdColSize} xl={lgColSize} key={renamedObj.label || renamedObj.value}>
                        <Form.Group
                            controlId={renamedObj.value}
                            key={renamedObj.value}
                            className={`form-group${pickerLayout === 'chart-settings' ? ' chart-settings-active-filter' : ''}`}
                        >
                            <Form.Label className='w-100 text-break'>
                                {dbRewritedFilterName ? dbRewritedFilterName[1] : (renamedObj.label || renamedObj.value)}
                                {
                                    pickerLayout !== 'chart-settings' && ['text_sample', 'summary_text_sample'].includes(renamedObj.type) ? (
                                        <Tooltip
                                            content={i18n.t('ft_how_to.text')}
                                            placement='top'
                                        >
                                            <button
                                                type='button'
                                                className='btn btn-link p-0 ml-2 text-info d-inline-flex align-items-center'
                                                onClick={() => setSamplesOpen(true)}
                                            >
                                                <Icon name="mdi-information-outline" size="1em" />
                                            </button>
                                        </Tooltip>
                                    ) : null
                                }
                                {pickerLayout !== 'chart-settings' && (
                                    <span style={{paddingRight: '5px'}}>
                                        <Tooltip
                                            content={obj.description || i18n.t('ft_no_desc.text')}
                                            placement='top'
                                        >
                                            <button
                                                type='button'
                                                className='btn btn-link p-0 ml-2 text-info d-inline-flex align-items-center'
                                                data-cy="event-filters-bundle-btn-2"
                                            >
                                                <Icon name="mdi-information-outline" size="1em" />
                                            </button>
                                        </Tooltip>
                                    </span>
                                )}
                                {
                                    pickerLayout === 'chart-settings' ? (
                                        <button
                                            type="button"
                                            className="chart-settings-active-filter__remove"
                                            data-cy={`${parent}-remove-event-filter-${obj.value.replace('all_fields.', '')}`}
                                            title={i18n.t('ft_remove_filter.text')}
                                            onClick={() => {
                                                onAdditionalFilterChange(
                                                    // @ts-ignore
                                                    produce(formValues.additionalFilters, (draft) => {
                                                        draft.splice(idx, 1);
                                                    })
                                                );
                                            }}
                                        >
                                            <Icon name="mdi-close" size="0.85em" />
                                        </button>
                                    ) : (
                                <Button
                                    data-cy={`${parent}-remove-event-filter-${obj.value.replace('all_fields.', '')}`}
                                    variant="link"
                                    size="xs"
                                    className='plain-button float-right'
                                    title={i18n.t('ft_remove_filter.text')}
                                    onClick={() => {
                                        onAdditionalFilterChange(
                                            // @ts-ignore
                                            produce(formValues.additionalFilters, (draft) => {
                                                draft.splice(idx, 1);
                                            })
                                        );
                                    }}
                                >
                                    <Icon name="mdi-close" className="text-danger" />
                                </Button>
                                    )
                                }
                            </Form.Label>
                            {pickerLayout === 'chart-settings' && renamedObj.type && filterSelectionTypes.includes(renamedObj.type) ? (
                                <ChartSettingsMultiFilterSelect
                                    formField={renamedObj}
                                    setFieldValue={(fieldName, fieldValue) => {
                                        if (!fieldValue.length || fieldValue[0].value === '') {
                                            const filteredValues = produce(formValues, (draft) => {
                                                const keys = fieldName.split('.');

                                                for (let i = 0; i < keys.length - 1; i++) {
                                                    if (!(draft as any)[keys[i]]) {
                                                        break;
                                                    }
                                                    draft = (draft as any)[keys[i]];
                                                }

                                                const finalKey = keys[keys.length - 1];
                                                delete (draft as any)[finalKey];
                                            });

                                            setValues(filteredValues);
                                        } else {
                                            setFieldValue(fieldName, fieldValue);
                                        }
                                    }}
                                    value={getValue(formValues, allFieldsName, renamedObj)}
                                    parent={parent}
                                />
                            ) : (
                                <SmartMultiInput
                                    formField={renamedObj}
                                    setFieldValue={(fieldName, fieldValue) => {
                                        if (!fieldValue.length || fieldValue[0].value === '') {
                                            const filteredValues = produce(formValues, (draft) => {
                                                const keys = fieldName.split('.');

                                                for (let i = 0; i < keys.length - 1; i++) {
                                                    if (!(draft as any)[keys[i]]) {
                                                        break;
                                                    }
                                                    draft = (draft as any)[keys[i]];
                                                }

                                                const finalKey = keys[keys.length - 1];
                                                delete (draft as any)[finalKey];
                                            });

                                            setValues(filteredValues);
                                        } else {
                                            setFieldValue(fieldName, fieldValue);
                                        }
                                    }}
                                    value={getValue(formValues, allFieldsName, renamedObj)}
                                    widgetResetKey={widgetsResetKey}
                                    parent={parent}
                                />
                            )}
                        </Form.Group>
                    </Col>
                );
            }))
        }
    }, [formValues, restrictedFieldKeys, eventFilterGroupList]);
    formValueKeys = (formValues.additionalFilters || []).map((obj: AdditionalFilter, idx: number) => obj.value);

    // Return early if still loading or no data - after all hooks have been called
    if (loading || !eventFilterGroupList) {
        return <div>{i18n.t('jt_loading_opt.text')}</div>;
    }

    if (pickerLayout === 'chart-settings') {
        return (
            <>
                <div className="cs-filters-wrap">
                    <div className="cs-filters-header">
                        <label className="cs-field__label">
                            {i18n.t('bt_filters.text')}
                            {filterCount > 0 && (
                                <span className="cs-filters-header__count">{filterCount}</span>
                            )}
                        </label>
                    </div>
                    {filterCount === 0 && (
                        <div className="cs-helper-text">
                            {i18n.t('cs_no_filters.text')}
                        </div>
                    )}
                    <ChartFilterDropdown
                        groups={chartSettingsFilterGroups}
                        onSelect={handleChartSettingsFilterSelect}
                        isLoading={loading}
                        parent={parent}
                    />
                </div>
                <div className='row mb-1' id='filters-collapse'>
                    {additionalFilters}
                </div>
            </>
        );
    }

    return (
        <>
            {enableCollapse &&
                <>
                    <a
                        onClick={() => toggleFiltersCollapse()}
                        aria-controls='filters-collapse'
                        aria-expanded={filtersOpen}
                        className='font-bold cursor-pointer mt-1'
                        id='filters-collapse-btn'
                    >
                        <span className='pr-2'> {i18n.t('bt_filters.text')} ({formValues.additionalFilters?.length})</span>
                        <Icon name={filtersOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'} />
                    </a>
                    <Button
                        variant="info"
                        onClick={() => toggleFiltersCollapse()}
                        data-cy='storyboard-filters-open-button'
                        id='storyboard-open-btn'
                    >
                        {filtersBtnText}
                    </Button>
                </>
            }
            <
                Collapse
                in={filtersOpen}
                className={!enableCollapse ? 'show' : ''}
            >
                <div className='row mb-1' id='filters-collapse'>
                    <Col xs={12} md={mdColSize} xl={lgColSize}>
                        <Form.Group controlId='additionalFilters' className='form-group'>
                            {
                                addFiltersLabelText ? (
                                    <Form.Label>
                                        {addFiltersLabelText}
                                        <Tooltip
                                            content={i18n.t('bt_to_make_search.text')}
                                            placement='top'
                                        >
                                            <button
                                                type='button'
                                                className='btn btn-link p-0 ml-2 text-info d-inline-flex align-items-center'
                                                data-cy="event-filters-bundle-btn-1"
                                            >
                                                <Icon name="mdi-information-outline" size="1em" />
                                            </button>
                                        </Tooltip>
                                    </Form.Label>
                                ) : null
                            }
                            {
                                oneFilterColSize != 12 ?
                                    <Form.Label className='w-100 text-break'>
                                        {i18n.t('bt_add_filter.text')}
                                        <Button
                                            variant="link"
                                            size="xs"
                                            className='plain-button float-right opacity-0 cursor-default'
                                            title=''
                                            disabled
                                            data-cy="event-filters-bundle-link-btn">
                                            <Icon name="mdi-close" className="text-danger" />
                                        </Button>
                                    </Form.Label>
                                    : <></>
                            }
                            <Select<SelectOption, true>
                                id={`${parent}-event-filters-dropdown`}
                                className='event-filters-select'
                                value={formValues.additionalFilters}
                                isMulti
                                isClearable={false}
                                name='additionalFilters'
                                onChange={onAdditionalFilterChange}
                                controlShouldRenderValue={false}
                                hideSelectedOptions={true}
                                isLoading={loading}
                                loadingMessage={() => i18n.t('jt_loading_opt.text')}
                                placeholder={eventFilterBundlePlaceholder}
                                getOptionValue={(option) => `${option.labelClean}`}
                                filterOption={createFilter({
                                    matchFrom: 'any',
                                    stringify: (option) => `${option.value}`,
                                })}
                                options={Object.entries(eventFilterGroupList || {}).map(([key, groupFilters]) => {
                                    let label;
                                    switch (key) {
                                        case 'core':
                                            label = i18n.t('ft_label_core.text');
                                            break;
                                        case 'prioritised':
                                            label = i18n.t('ft_label_evaluations.text');
                                            break;
                                        case 'non_prioritised':
                                            label = i18n.t('ft_label_evaluations_not_prioritised.text');
                                            break;
                                        case 'others':
                                            label = i18n.t('ft_label_others.text');
                                            break;
                                        default:
                                            label = key;
                                    }
                                    return {
                                        label,
                                        options: (groupFilters || [])
                                            .filter((el) => el && el[4] && !formValueKeys.includes(el[0]))
                                            .map((el) => {
                                                const restricted = isFilterRestricted(el);
                                                return {
                                                    value: el[0].replace('all_fields.', `${allFieldsName}.`),
                                                    label: (
                                                        <>
                                                            <span className={restricted ? 'd-inline-flex align-items-center' : ''} style={{paddingRight: '5px', ...(restricted ? {color: '#999'} : {})}}>
                                                                {el[1] || el[0]}
                                                                {restricted && <Icon name="mdi-lock" size="0.85em" className="ml-1" />}
                                                                {key === 'prioritised' && <PrioritiseButton isPriority={true} />}
                                                                {!restricted && (
                                                                    <Tooltip
                                                                        content={el[3] ? el[3].replace(/<[^>]+>/g, '') : i18n.t('ft_no_desc.text')}
                                                                        placement='top'
                                                                    >
                                                                        <button
                                                                            type='button'
                                                                            className='btn btn-link p-0 ml-2 text-info d-inline-flex align-items-center'
                                                                            data-cy="event-filters-bundle-btn">
                                                                            <Icon name="mdi-information-outline" size="1em" />
                                                                        </button>
                                                                    </Tooltip>
                                                                )}
                                                            </span>
                                                        </>
                                                    ),
                                                    type: el[2] as ChartFilterType,
                                                    labelClean: el[1] || el[0],
                                                    description: el[3] ? el[3].replace(/<[^>]+>/g, '') : i18n.t('ft_no_desc.text'),
                                                    isDisabled: restricted,
                                                };
                                            }),
                                    };
                                })}
                                isOptionDisabled={(option) => !!option.isDisabled}
                                styles={{
                                    option: (base: CSSObject, state: OptionProps<SelectOption, true>): CSSObject => {
                                        let additionalStyles: CSSObject;
                                        if (state.data?.isDisabled) {
                                            additionalStyles = {
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                cursor: 'not-allowed',
                                            };
                                        } else if (!state.isFocused) {
                                            additionalStyles = {
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            };
                                        } else {
                                            additionalStyles = {
                                                wordBreak: 'break-word',
                                            };
                                        }
                                        return {
                                            ...base,
                                            ...additionalStyles,
                                        };
                                    },
                                }}
                            />

                        </Form.Group>
                    </Col>

                    {additionalFilters}

                    {saveButtonContent && (
                        <Col xs={12}>
                            <div className="mt-3">
                                {saveButtonContent}
                            </div>
                        </Col>
                    )}
                </div>
            </Collapse>
        </>
    );
}

export default React.memo(EventFiltersBundle) as typeof EventFiltersBundle;